import apiClient from './config';
import { Suscripcion as Subscription } from "../../types/index";
import type { Cotizacion, LineaDesglose } from "../../types/suscripciones";

export const createSubscription = async (subscriptionData: Partial<Subscription>): Promise<Subscription> => {
    const response = await apiClient.post('/subscriptions', subscriptionData);
    return response.data.subscription;
};

export const updateSubscription = async (id: string, data: Partial<Subscription>): Promise<Subscription> => {
    const response = await apiClient.put(`/subscriptions/${id}`, data);
    return response.data.subscription;
};

export const deleteSubscription = async (id: string): Promise<void> => {
    await apiClient.delete(`/subscriptions/${id}`);
};

export const updateBeneficiario = async (id: string, beneficiarioId: string): Promise<Subscription> => {
    const response = await apiClient.put(`/subscriptions/${id}/beneficiario/${beneficiarioId}`);
    return response.data.subscription;
};

// Función para obtener una suscripción específica
export const fetchSubscriptionById = async (id: string) => {
    const response = await apiClient.get(`/subscriptions/${id}`);
    return response.data;
};

// Actualizar la frecuencia según el número de plazas
export const actualizarFrecuenciaPorPlazas = async (params: {
    suscripcionId: string;
    clientId: string;
    updatedBy?: string;
}): Promise<any> => {
    const { suscripcionId, clientId, updatedBy } = params;
    
    const response = await apiClient.patch(`/subscriptions/${suscripcionId}/actualizar-frecuencia`, {
        clientId,
        updatedBy: updatedBy || 'sistema'
    });
    
    return response.data;
};

// Precio de un conjunto de plazas, sin guardar nada. Con `suscripcionId` y sin
// `tarifasAsignadas`, el back usa las tarifas que ya tiene esa suscripción.
export const cotizarSuscripcion = async (params: {
    plazas: Array<{ nivel_id: string }>;
    tarifasAsignadas?: string[];
    suscripcionId?: string;
}): Promise<Cotizacion> => {
    const response = await apiClient.post('/subscriptions/cotizar', params);
    return response.data;
};

// Aplica las tarifas vigentes a clientes que ya existen, desde sus plazas.
// Crear o editar tarifas no cambia a nadie de importe: esto es lo que lo hace.
// Sin `suscripcionId` es todo el centro (solo admin). `dryRun` por defecto.
export interface RecalculoDesdePlazas {
    dryRun: boolean;
    actualizadas: number;
    total: number;
    cambios: Array<{
        id: string;
        cliente: string;
        frecuenciaAntes: string;
        frecuencia: string;
        antes: number;
        despues: number;
        desglose: LineaDesglose[];
    }>;
    sinCambios: number;
    omitidas: { precioFijo: number; ilimitada: number; sinPlazas: number };
    faltaElegir: Array<{ id: string; cliente: string; opciones: string[] }>;
    sinPrecio: Array<{ id: string; cliente: string; motivo: string }>;
}

export const recalcularDesdePlazas = async (params: {
    dryRun?: boolean;
    suscripcionId?: string;
    tarifasAsignadas?: string[];
}): Promise<RecalculoDesdePlazas> => {
    const response = await apiClient.post('/subscriptions/recalcular-desde-plazas', params);
    return response.data;
};

// Interfaces para actualización masiva de tarifas
export interface BulkUpdateTariffsRequest {
    nuevasTarifas: Array<{ weeklySessions: number; price    : number }>;
    filtros?: {
        estado?: string;
        tipo?: string;
        soloActivos?: boolean;
    };
    updatedBy?: string;
    dryRun?: boolean;
}

export interface BulkUpdateTariffsResponse {
    success: boolean;
    actualizadas: number;
    totalSuscripciones?: number;
    noActualizadas?: number;
    dryRun?: boolean;
    message?: string;
    detalle?: {
        porTipo?: Record<string, number>;
        porFrecuencia?: Record<string, number>;
    };
    cambios?: Array<{
        suscripcionId: string;
        clienteNombre: string;
        frecuencia: string;
        tarifaAnterior: number;
        tarifaNueva: number;
    }>;
    errores?: Array<{
        suscripcionId: string;
        error: string;
    }>;
}

// Actualizar tarifas de forma masiva
export const bulkUpdateTariffs = async (data: BulkUpdateTariffsRequest): Promise<BulkUpdateTariffsResponse> => {
    const response = await apiClient.post('/subscriptions/bulk-update-tariffs', data);
    return response.data;
};